import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';

import { AssetViewer } from '../../AssetViewer';
import { EmbedContext } from './context';
import FileService from '../../../components/AssetViewer/helper/fileHelper';

const ContentContext = React.createContext({
  debug: false,
  fetchMappingsForContentGrid: (contentGrid, page, limit) => { },
  fetchPreviewDataForAsset: (asset) => { },
  updateFiltersInContenGrid: (contentGrid, filters) => { },
  updateQueryInContenGrid: (contentGrid, query) => { },
  resetFiltersInContenGrid: (contentGrid) => { },
  viewContent: (widget, content) => { },
  onContentChange: (contentGrid, content) => { },
  onDownload: (asset) => { }
});

const ContentManager = ({
  initialAsset,
  page,
  closeAssetViewer,
  children,
  fetchMappingsForContentGrid,
  fetchPreviewDataForAsset,
  updateFiltersInContenGrid,
  updateQueryInContenGrid,
  resetFiltersInContenGrid,
  onContentChange,
  onAnalyticsChange,
  fetchAssetJourney,
  updateAssetJourneyExecution,
  onDownload,
  onClose,
  debug
}) => {
  const [assetViewer, setAssetViewer] = useState(null);

  const embedContext = React.useContext(EmbedContext);

  useEffect(() => {
    setAssetViewer(initialAsset);
  }, []);

    /** Close an asset for mandatory gating */
    useEffect(() => {
      if (closeAssetViewer) {
        handleCloseViewer();
      }
    }, [closeAssetViewer])

    /** Set the assetViewer attribute on the body */
    useEffect(() => {
      if(assetViewer) document.body?.setAttribute("assetViewer", "true");
      else document.body?.removeAttribute("assetViewer");
    }, [assetViewer]);

    var pageSession = useSelector((state) =>
      _.get(state, `pages.analytics[${page}].id`, null)
    )

  const handleViewContent = (widget, content) => {

    if (!content) {
      return;
    }

    const newTab = content.content.newTab;
    const currentWindow = window.self || window;
    const excludeTypes = ['YOUTUBE', 'VIMEO', 'WISTIA', 'VIDYARD'];
    const asset = _.get(content, 'content.asset');
    const viewerMode = _.get(asset, 'viewer.mode');
    const extension = FileService.fileType(_.get(asset, 'metadata'));
    
    const shouldRender = ['render_in_viewer', 'render_optimized_view'].includes(viewerMode) || excludeTypes.includes(extension);

    if (embedContext.embedded) {
      if (!content || !content.url) {
        return;
      }
      const currentWindow = window.self || window;
      const contentType = _.get(content, 'type', '');
      let pageUrl = `${_.get(content, 'content.domain', '')}${_.get(content, 'url', '')}?page=${page}&contentGrid=${widget}&pageSession=${pageSession}`;

      if (['INTERACTIVE_CONTENT', 'LANDING_PAGE', 'RESOURCE_CENTER', 'SPATIAL'].includes(contentType)) {
        pageUrl = `${_.get(content, 'content.domain', '')}${_.get(content, 'url', '')}`;
      }

      if (newTab && viewerMode === "open_in_new_tab"){
        pageUrl = _.get(content, 'content.asset.metadata.url', '');
      }

      var isInIframe = currentWindow.parent !== window;
      if (isInIframe) {
        const parentWindow = window.parent;
        parentWindow.postMessage({
          type: "cleverstory:embed:assetViewer",
          payload: {
            pageUrl: pageUrl,
            pageSession: pageSession,
            contentType: contentType,
            newTab: shouldRender ? false : newTab
          }
        }, '*');
      }
      return;
    }

    if (!content.url) {
      return;
    }
    let pageUrl = `${_.get(content, 'content.domain', '')}${_.get(content, 'url', '')}`;
    const pageTypes = ['INTERACTIVE_CONTENT', 'LANDING_PAGE', 'RESOURCE_CENTER'];
    
    if (shouldRender) {
      setAssetViewer({
        asset: content,
        contentGrid: widget,
        redirect: true
      });
      return;
    }

    if (newTab) {
      if (viewerMode === "open_in_new_tab"){
        pageUrl = _.get(content, 'content.asset.metadata.url', '');
      }
      window.open(pageUrl);
    } else if (pageTypes.includes(content.type)) {
      currentWindow.location = pageUrl;
    } else if (content.type === 'ASSET') {
      setAssetViewer({
        asset: content,
        contentGrid: widget,
        redirect: true
      });
    }
  }

  const handleCloseViewer =()=>{
    setAssetViewer(null);
    var isInIframe = window.parent !== window;
    if (isInIframe) {
      const parentWindow = window.parent;
      parentWindow.postMessage({
        type: "cleverstory:close:assetViewer",
      }, '*');
    }

    if (onClose) {
      onClose()
    }
  }

  const contentContextValue = React.useMemo(() => ({
    debug: debug,
    page: page,
    initialAsset: initialAsset,
    fetchMappingsForContentGrid: fetchMappingsForContentGrid,
    fetchPreviewDataForAsset: fetchPreviewDataForAsset,
    updateFiltersInContenGrid: updateFiltersInContenGrid,
    updateQueryInContenGrid: updateQueryInContenGrid,
    resetFiltersInContenGrid: resetFiltersInContenGrid,
    viewContent: handleViewContent,
    onContentChange: onContentChange,
    onAnalyticsChange: onAnalyticsChange,
    fetchAssetJourney: fetchAssetJourney,
    updateAssetJourneyExecution: updateAssetJourneyExecution,
    onDownload: onDownload
  }), [debug, handleViewContent]);

  return (
    <ContentContext.Provider value={contentContextValue}>
      {children}
      {assetViewer &&
        <AssetViewer
          contentGrid={assetViewer.contentGrid}
          redirect={assetViewer.redirect}
          show={true}
          initialContent={_.get(assetViewer, 'asset')}
          debug={debug}
          onClose={handleCloseViewer}
        />
      }
    </ContentContext.Provider>
  );
}

const Content = {
  Manager: ContentManager,
  Context: ContentContext
}

export default Content;
